// In-memory ring buffer sink.
//
// Used for local development before a real vendor (NewRelic/Sentry) is set
// up. Holds the last MEMORY_RING_SIZE records and exposes a read-only
// accessor via `window.__zopeoLogs(...filters)` so developers can inspect
// log activity from DevTools without the app itself writing to console.
//
// Never enabled in production builds.

import type { LogRecord, Sink } from '../transport';
import { logEnv } from '../env';

const MEMORY_RING_SIZE = 500;

const ring: LogRecord[] = [];

function push(r: LogRecord): void {
  ring.push(r);
  if (ring.length > MEMORY_RING_SIZE) ring.shift();
}

export function snapshotMemoryRing(): LogRecord[] {
  return ring.slice();
}

export function clearMemoryRing(): void {
  ring.length = 0;
}

export function createMemorySink(): Sink {
  return (batch: LogRecord[]) => {
    for (const r of batch) push(r);
  };
}

/**
 * Expose `window.__zopeoLogs` in dev only.
 *
 * Usage in DevTools:
 *   __zopeoLogs()                       → all in-memory records
 *   __zopeoLogs({ level: 'error' })    → filter by level
 *   __zopeoLogs({ event: /^auth_/ })   → regex match on event
 *   __zopeoLogs({ module: 'billing' }) → filter by module
 *   __zopeoLogs.clear()                → wipe the ring
 */
export function installDevAccessor(): void {
  if (logEnv.env === 'prod') return;
  if (typeof window === 'undefined') return;
  type Filter = {
    level?: LogRecord['level'];
    event?: string | RegExp;
    module?: string;
    correlationId?: string;
  };
  const accessor = (filter?: Filter): LogRecord[] => {
    let out = ring.slice();
    if (!filter) return out;
    if (filter.level) out = out.filter((r) => r.level === filter.level);
    if (filter.event) {
      const e = filter.event;
      out = out.filter((r) => (typeof e === 'string' ? r.event === e : e.test(r.event)));
    }
    if (filter.module) out = out.filter((r) => r.module === filter.module);
    if (filter.correlationId) out = out.filter((r) => r.correlationId === filter.correlationId);
    return out;
  };
  (accessor as unknown as { clear: () => void }).clear = clearMemoryRing;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (window as any).__zopeoLogs = accessor;
}
